import { SenderSource } from '@enuan/chat__utils';
import { capitalize } from 'lodash';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { messageMessages } from 'src/components/message/message.i18n';

/**
 * Returns a getter that resolves the localized sender name from SenderSource.
 * Use this when you need to call the resolver after conditional logic (e.g. after an early return).
 *
 * **Behaviour:**
 * - Uses react-intl for default labels (sender__agent, sender__system, sender__user).
 * - The returned getter maps SenderSource.AGENT → agent message, SYSTEM → system, USER → user; for any other value returns capitalize(sender).
 * - Result updates on locale change via the getter's closure.
 *
 * @returns {(sender: SenderSource) => string} getSenderName(sender) returns the sender name to display in the UI
 */
export const useSenderName = () => {
	const intl = useIntl();

	const getSenderName = useCallback(
		(sender: SenderSource) => {
			switch (sender) {
				case SenderSource.AGENT:
					return intl.formatMessage(messageMessages.sender__agent);
				case SenderSource.SYSTEM:
					return intl.formatMessage(messageMessages.sender__system);
				case SenderSource.USER:
					return intl.formatMessage(messageMessages.sender__user);
				default:
					return capitalize(sender);
			}
		},
		[intl],
	);
	return getSenderName;
};
